import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Sparkles, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { req } from '../services/api';


/**
 * Botón + tarjeta con resumen ejecutivo (3 bullets) generado por Gemini
 * sobre el estado relacional con un contacto. Combina correos + visitas + tareas.
 *
 * Props:
 *   - contactoId
 *   - autoLoad: si true, genera al montar (default false — el usuario lo pide).
 */
export default function ResumenContactoIA({ contactoId, autoLoad = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generar = async () => {
    if (!contactoId) return;
    setLoading(true);
    try {
      const r = await req('/api/correo/ai/resumen-contacto', {
        method: 'POST',
        body: JSON.stringify({ contacto_id: contactoId }),
      });
      setData(r);
    } catch (e) {
      toast.error(`IA: ${e.message}`);
    } finally { setLoading(false); }
  };

  React.useEffect(() => {
    if (autoLoad && !data && !loading) { generar(); }
    /* eslint-disable-next-line */
  }, [autoLoad, contactoId]);

  if (!data && !loading) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={generar}
        className="text-violet-700 border-violet-200 hover:bg-violet-50"
        data-testid="btn-resumen-contacto"
      >
        <Sparkles className="w-4 h-4 mr-1.5" /> Resumen ejecutivo IA
      </Button>
    );
  }

  if (loading && !data) {
    return (
      <div className="text-xs text-ink-500 flex items-center gap-1.5" data-testid="resumen-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generando resumen…
      </div>
    );
  }

  return (
    <Card className="p-3 border-violet-200 bg-violet-50/40 mt-2" data-testid="resumen-contacto-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-violet-700">
          <Sparkles className="w-3.5 h-3.5" /> Resumen ejecutivo IA
        </div>
        <Button size="sm" variant="ghost" onClick={generar} disabled={loading} title="Regenerar" data-testid="resumen-regenerar">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>

      <ul className="space-y-1.5 mb-2">
        {data.bullets.map((b, i) => (
          <li key={i} className="text-xs text-ink-800 flex items-start gap-2" data-testid={`resumen-bullet-${i}`}>
            <span className="text-violet-600 mt-0.5">▸</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {data.alerta && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="resumen-alerta">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{data.alerta}</span>
        </div>
      )}

      <div className="text-[10px] text-ink-400 mt-2 flex flex-wrap gap-2">
        <Badge className="bg-ink-50 text-ink-600 border-ink-200 border text-[9px]">
          {data.datos_usados?.correos || 0} correos
        </Badge>
        <Badge className="bg-ink-50 text-ink-600 border-ink-200 border text-[9px]">
          {data.datos_usados?.visitas || 0} visitas
        </Badge>
        <Badge className="bg-ink-50 text-ink-600 border-ink-200 border text-[9px]">
          {data.datos_usados?.tareas_pendientes || 0} tareas pdtes
        </Badge>
      </div>
    </Card>
  );
}
